import React, { useId, useMemo, useRef, useState } from "react";
import { useInstagramApiPublishingOn } from "../../../../../utils/feature-toggle-utils";
import { useDebouncedCallback } from "use-debounce";

import { FIELDS } from "../../../../../constants/PostFormFields";
import {
  apiCapableInstagramAccounts,
  type TagCapabilityAccount,
} from "../../PostTags/TagCapabilityWarning";
import {
  isInstagramReelMedia,
  selectedInstagramAccounts,
} from "../InstagramPlacementControl/InstagramPlacementControl";
import type { IgAudioTrack, IgAudioType } from "../../../../../api/post";
import type { PostMediaFile } from "../../../../../shared/types/post";
import type { AccountPlatformType } from "../../../../../shared/types/accounts";
import { Alert, AlertTitleRow } from "../../../../ui/alert";
import { Input } from "../../../../ui/input";
import { SearchInput } from "../../../../ui/search-input";
import { Slider } from "../../../../ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../ui/tabs";
import { INSTAGRAM_ONLY } from "../PlatformLabelBadges/PlatformLabelBadges";
import { InfoTooltip, SwitchFieldRow } from "../SwitchFieldRow/SwitchFieldRow";
import { AudioRemoveButton } from "./AudioIconAction";
import { AudioTrackList, TrackArtwork } from "./AudioTrackList";
import { InstagramLookupNotice } from "../InstagramLookupNotice/InstagramLookupNotice";
import { trackAttribution, trackTitle, useAudioCatalog } from "./audio-catalog";

/** Meta's volumes run from 0 to 100, and full is the volume nobody has to think about. */
const FULL_VOLUME = 100;

const CATALOGUES: { value: IgAudioType; label: string }[] = [
  { value: "music", label: "Music" },
  { value: "original_sound", label: "Original audio" },
];

interface InstagramAudioPickerProps {
  mediaFiles: PostMediaFile[];
  selectedPlatform: AccountPlatformType | "LINKED";
  formAccounts: TagCapabilityAccount[];
  updateField: (field: string, value: unknown) => void;
  /**
   * Whether the switch is on with no track chosen yet. Held by the form, not
   * here: the composer remounts this picker on every platform tab switch.
   */
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  platformPost?: any;
}

const VolumeSlider: React.FC<{
  id: string;
  label: string;
  value: number;
  disabled?: boolean;
  onChange(value: number): void;
}> = ({ id, label, value, disabled, onChange }) => (
  <div className="flex flex-col gap-1.5">
    <div className="flex items-center justify-between font-inter text-xs leading-5">
      <span id={id} className="font-medium text-neutral-800">
        {label}
      </span>
      <span className="tabular-nums text-neutral-600">{value}</span>
    </div>
    <Slider
      min={0}
      max={100}
      step={1}
      value={[value]}
      disabled={disabled}
      thumbProps={{ "aria-labelledby": id }}
      onValueChange={([next]) => onChange(next)}
    />
  </div>
);

/**
 * SKE-4542. Attaches a licensed Instagram track to this reel.
 *
 * Reel-only, because `validateContainerRequest` refuses audio on any other
 * target with "Choosing an audio track is a reels-only setting.", and that
 * refusal fails the whole post. Same reel condition as the trial reel control,
 * from the same helper, so the two cannot drift apart.
 *
 * A track's `downloadUrl` expires in about 36 hours, so nothing here writes one
 * into form state. The picker holds the loaded tracks, and the one chosen, in
 * memory for as long as the composer is open and writes only the `audioId` and
 * the two volumes.
 */
export const InstagramAudioPicker: React.FC<InstagramAudioPickerProps> = ({
  mediaFiles,
  selectedPlatform,
  formAccounts,
  updateField,
  expanded,
  onExpandedChange,
  disabled,
  platformPost,
}) => {
  const apiPublishingOn = useInstagramApiPublishingOn();
  const fieldId = useId();
  const searchRef = useRef<HTMLInputElement>(null);
  const [audioType, setAudioType] = useState<IgAudioType>("music");
  const [query, setQuery] = useState("");
  const [previewingAudioId, setPreviewingAudioId] = useState<
    string | undefined
  >();
  const [chosenTrack, setChosenTrack] = useState<IgAudioTrack | undefined>();

  const igAccounts = useMemo(
    () => selectedInstagramAccounts(formAccounts),
    [formAccounts]
  );
  const capableAccountIds = useMemo(
    () =>
      apiCapableInstagramAccounts(igAccounts, apiPublishingOn)
        .map((account) => account._id)
        .filter((id): id is string => Boolean(id)),
    [igAccounts, apiPublishingOn]
  );

  const show = useMemo(() => {
    if (selectedPlatform !== "IG" && selectedPlatform !== "LINKED")
      return false;
    if (platformPost?.isStory) return false;
    if (!isInstagramReelMedia(mediaFiles)) return false;
    return capableAccountIds.length > 0;
  }, [
    selectedPlatform,
    platformPost?.isStory,
    mediaFiles,
    capableAccountIds.length,
  ]);

  const configuration = platformPost?.instagram?.audioConfiguration;
  const audioMeta = platformPost?.instagram?.audioMeta;
  const selectedAudioId: string | undefined = configuration?.audioId;
  const audioName: string = platformPost?.instagram?.audioName ?? "";
  const open = expanded || Boolean(selectedAudioId);

  const { state, search, loadMore } = useAudioCatalog(
    open && show ? capableAccountIds : [],
    audioType
  );

  const debouncedSearch = useDebouncedCallback(search, 400);

  const selectedTrack: IgAudioTrack | undefined =
    chosenTrack && chosenTrack.audioId === selectedAudioId
      ? chosenTrack
      : state.kind === "ready"
        ? state.tracks.find((track) => track.audioId === selectedAudioId)
        : undefined;

  if (!show) return null;

  const rejectedAccounts =
    state.kind === "ready" || state.kind === "unavailable"
      ? state.rejectedAccounts
      : [];
  const allRejected =
    state.kind === "unavailable" &&
    state.allRejected &&
    rejectedAccounts.length > 0;

  const writeVolume = (key: "audioVolume" | "videoVolume", value: number) => {
    if (!selectedAudioId) return;
    updateField(FIELDS.IG_AUDIO_CONFIGURATION, {
      audioId: selectedAudioId,
      audioVolume: configuration?.audioVolume ?? FULL_VOLUME,
      videoVolume: configuration?.videoVolume ?? FULL_VOLUME,
      [key]: value,
    });
  };

  const clearTrack = () => {
    updateField(FIELDS.IG_AUDIO_CONFIGURATION, undefined);
    updateField(FIELDS.IG_AUDIO_NAME, undefined);
    // Cleared with the configuration, always. Metadata describes a track that
    // the configuration attaches, so leaving it behind would let the preview
    // name a track that will not publish.
    updateField(FIELDS.IG_AUDIO_META, undefined);
    setChosenTrack(undefined);
  };

  const handleToggle = (next: boolean) => {
    onExpandedChange(next);
    if (next) return;

    setPreviewingAudioId(undefined);
    clearTrack();
  };

  const handleSelect = (track: IgAudioTrack) => {
    if (!track.audioId) return;
    setChosenTrack(track);
    updateField(FIELDS.IG_AUDIO_CONFIGURATION, {
      audioId: track.audioId,
      audioVolume: configuration?.audioVolume ?? FULL_VOLUME,
      videoVolume: configuration?.videoVolume ?? FULL_VOLUME,
    });
    /*
     * SKE-4969. Persist enough to NAME the track later. `chosenTrack` lives in
     * component state, so before this the title survived only until the form
     * remounted, and every other surface had to say "saved track".
     *
     * The download url is deliberately absent: it expires in about a day and a
     * half, so storing it would guarantee a dead link rather than a fallback.
     */
    updateField(FIELDS.IG_AUDIO_META, {
      artist: trackAttribution(track),
      artworkUrl: track.coverArtworkThumbnailUri || track.profilePictureUrl,
      title: trackTitle(track),
    });
    /*
     * The rename belongs to the track it renamed. Switching tracks used to
     * leave it in place, which was survivable while nothing displayed it, and
     * is not now: the preview gives a rename precedence over the stored title,
     * so a stale one would label the NEW track with the OLD name.
     */
    updateField(FIELDS.IG_AUDIO_NAME, undefined);
  };

  const handleRemove = () => {
    onExpandedChange(true);
    setPreviewingAudioId(undefined);
    clearTrack();
    searchRef.current?.focus();
  };

  /*
   * SKE-4969. `selectedTrack` is only the track picked in THIS session, so a
   * reopened post used to fall back to "saved track" and tell the customer
   * nothing about what they had chosen. The stored metadata names it.
   */
  const chosenTitle = selectedTrack
    ? trackTitle(selectedTrack)
    : audioMeta?.title || "saved track";

  return (
    <SwitchFieldRow
      data-testid="ig-audio-picker"
      id={fieldId}
      label="Add a track from Instagram's audio"
      helper="This is the catalogue Instagram has licensed for scheduling. It is not the library you browse in the Instagram app, so a track you can hear there may not be here."
      checked={open}
      disabled={disabled}
      selectedPlatform={selectedPlatform}
      platforms={INSTAGRAM_ONLY}
      onCheckedChange={handleToggle}
    >
      {open ? (
        <div className="flex flex-col gap-3">
          {selectedAudioId ? (
            <div
              className="inline-flex w-fit max-w-full items-center gap-2 self-start rounded-md border border-solid border-neutral-200 bg-white py-0.5 pl-2.5 pr-0.5"
              data-testid="ig-audio-chosen-chip"
            >
              <TrackArtwork track={selectedTrack} className="size-7" />
              <p className="m-0 min-w-0 grow truncate font-inter text-[13px] leading-5 text-neutral-800">
                <span className="text-neutral-600">Attached: </span>
                <span className="font-medium">{chosenTitle}</span>
              </p>
              <AudioRemoveButton
                title={chosenTitle}
                disabled={disabled}
                onRemove={handleRemove}
              />
            </div>
          ) : null}
          {allRejected ? (
            <div role="status" aria-live="polite">
              <InstagramLookupNotice
                kind="audio"
                rejected={rejectedAccounts}
                formAccounts={formAccounts}
                outcome="all"
              />
            </div>
          ) : (
            <Tabs
              className="flex flex-col gap-2"
              value={audioType}
              onValueChange={(next) => {
                setPreviewingAudioId(undefined);
                setAudioType(next as IgAudioType);
              }}
            >
              <TabsList
                size="sm"
                aria-label="Audio catalogue"
                className="flex w-full"
              >
                {CATALOGUES.map((catalogue) => (
                  <TabsTrigger
                    key={catalogue.value}
                    value={catalogue.value}
                    disabled={disabled}
                    className="flex-1"
                  >
                    {catalogue.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent
                value={audioType}
                tabIndex={undefined}
                className="m-0 flex flex-col gap-2"
              >
                <SearchInput
                  ref={searchRef}
                  value={query}
                  disabled={disabled}
                  placeholder="Search tracks"
                  aria-label="Search tracks"
                  onChange={(event) => {
                    setQuery(event.target.value);
                    debouncedSearch(event.target.value);
                  }}
                />

                {audioType === "original_sound" ? (
                  <p className="m-0 font-inter text-xs leading-5 text-neutral-600">
                    Most original audio can only be heard on Instagram, so those
                    tracks open in a new tab.
                  </p>
                ) : null}

                <div role="status" aria-live="polite" className="empty:-mt-2">
                  {rejectedAccounts.length > 0 ? (
                    <InstagramLookupNotice
                      kind="audio"
                      rejected={rejectedAccounts}
                      formAccounts={formAccounts}
                      outcome={
                        state.kind === "unavailable" ? "failed" : "results"
                      }
                    />
                  ) : null}
                </div>

                {state.kind === "unavailable" ? (
                  <Alert
                    variant="warning"
                    role="status"
                    aria-live="polite"
                    data-testid="ig-audio-unavailable"
                  >
                    <AlertTitleRow variant="warning" className="font-inter">
                      {state.serverMessage ??
                        "We could not reach Instagram's audio catalogue just now."}
                    </AlertTitleRow>
                  </Alert>
                ) : (
                  <AudioTrackList
                    tracks={state.kind === "ready" ? state.tracks : []}
                    busy={
                      state.kind === "loading" ||
                      (state.kind === "ready" && state.busy)
                    }
                    hasMore={state.kind === "ready" && Boolean(state.after)}
                    autoPaused={state.kind === "ready" && state.autoPaused}
                    exhausted={state.kind === "ready" && state.exhausted}
                    searched={
                      state.kind === "ready" && state.query.trim().length > 0
                    }
                    resetKey={
                      state.kind === "ready" ? state.searchId : undefined
                    }
                    selectedAudioId={selectedAudioId}
                    previewingAudioId={previewingAudioId}
                    disabled={disabled}
                    loadMoreError={
                      state.kind === "ready" ? state.loadMoreError : undefined
                    }
                    onSelect={handleSelect}
                    onPreview={(track) =>
                      setPreviewingAudioId(
                        previewingAudioId === track.audioId
                          ? undefined
                          : track.audioId
                      )
                    }
                    onPreviewEnded={() => setPreviewingAudioId(undefined)}
                    onLoadMore={loadMore}
                  />
                )}
              </TabsContent>
            </Tabs>
          )}
          {selectedAudioId ? (
            <div className="flex flex-col gap-3" data-testid="ig-audio-chosen">
              <VolumeSlider
                id={`${fieldId}-audio-volume`}
                label="Track volume"
                value={configuration?.audioVolume ?? FULL_VOLUME}
                disabled={disabled}
                onChange={(value) => writeVolume("audioVolume", value)}
              />
              <VolumeSlider
                id={`${fieldId}-video-volume`}
                label="Your video's volume"
                value={configuration?.videoVolume ?? FULL_VOLUME}
                disabled={disabled}
                onChange={(value) => writeVolume("videoVolume", value)}
              />

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-1.5">
                  <label
                    className="m-0 font-inter text-xs font-medium leading-5 text-neutral-800"
                    htmlFor={`${fieldId}-audio-name`}
                  >
                    Audio name{" "}
                    <span className="font-normal text-neutral-600">
                      (optional)
                    </span>
                  </label>
                  <InfoTooltip label="About the audio name">
                    The name people see on this reel&apos;s audio in Instagram.
                    Leave it blank to keep the name Instagram gives it.
                  </InfoTooltip>
                </div>
                <Input
                  id={`${fieldId}-audio-name`}
                  type="text"
                  disabled={disabled}
                  value={audioName}
                  placeholder="Instagram's default name"
                  className="h-9 font-inter"
                  aria-describedby={`${fieldId}-audio-name-helper`}
                  onChange={(event) =>
                    updateField(
                      FIELDS.IG_AUDIO_NAME,
                      event.target.value || undefined
                    )
                  }
                />
                <p
                  id={`${fieldId}-audio-name-helper`}
                  className="m-0 font-inter text-xs leading-5 text-neutral-600"
                >
                  Instagram lets you name the audio once and never again, so
                  check it before you publish.
                </p>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </SwitchFieldRow>
  );
};

export default InstagramAudioPicker;
